/**
 * ファイル種別に応じた Material Symbols Rounded のアイコン名を返す共通ユーティリティ。
 *
 * ファイル名を表示する UI では、先頭アイコンのルールを以下に揃える。
 * - A-1: その場で再生できる音声（AudioPreview の compactAudio など）
 *         → 本ユーティリティではなく、個別に play_circle / pause_circle を使用する。
 * - A-2: 再生 UI を持たない一覧・アップロード前後・選択プレビュー等
 *         → 本ユーティリティのタイプアイコンを使用する。
 * - A-3: モーダルタイトル等
 *         → アイコンなし（本ユーティリティ不使用）。
 */

/** アプリ共通で使用する Material Symbol アイコン名の定数 */
export const FILE_ICONS = {
  image: 'image',
  video: 'movie',
  audio: 'audio_file',
  pdf: 'picture_as_pdf',
  document: 'description',
  spreadsheet: 'table_chart',
  slideshow: 'slideshow',
  code: 'code',
  archive: 'folder_zip',
  error: 'error',
  // ファイル名先頭に置く「汎用ファイル」アイコン。
  // attach_file（ペーパークリップ）は「添付する」アクションと意味が重なるため、
  // 種別判定に失敗した場合のフォールバックは汎用の書類アイコンに寄せる。
  unknown: 'insert_drive_file',
} as const;

export type FileIconName = (typeof FILE_ICONS)[keyof typeof FILE_ICONS];

// バックエンド API で使われるカテゴリ文字列（ProjectMessageFile.file_type 等）→ アイコン名。
// 'document' はアプリ内で実質的に PDF として扱われる（MessageItem で mediaType='pdf' にマッピングしている）ため、
// 先頭アイコンも picture_as_pdf に統一する。
const CATEGORY_ICON_MAP: Record<string, string> = {
  image: FILE_ICONS.image,
  video: FILE_ICONS.video,
  audio: FILE_ICONS.audio,
  document: FILE_ICONS.pdf,
  pdf: FILE_ICONS.pdf,
  spreadsheet: FILE_ICONS.spreadsheet,
  archive: FILE_ICONS.archive,
};

/** MediaType（画像/動画/音声/PDF）→ アイコン名 */
const MEDIA_TYPE_ICON_MAP: Record<string, string> = {
  image: FILE_ICONS.image,
  video: FILE_ICONS.video,
  audio: FILE_ICONS.audio,
  pdf: FILE_ICONS.pdf,
};

const EXTENSION_ICON_MAP: Record<string, string> = {
  jpg: FILE_ICONS.image,
  jpeg: FILE_ICONS.image,
  png: FILE_ICONS.image,
  gif: FILE_ICONS.image,
  webp: FILE_ICONS.image,
  svg: FILE_ICONS.image,
  bmp: FILE_ICONS.image,
  heic: FILE_ICONS.image,
  heif: FILE_ICONS.image,
  mp4: FILE_ICONS.video,
  webm: FILE_ICONS.video,
  mov: FILE_ICONS.video,
  m3u8: FILE_ICONS.video,
  avi: FILE_ICONS.video,
  mkv: FILE_ICONS.video,
  mp3: FILE_ICONS.audio,
  wav: FILE_ICONS.audio,
  aac: FILE_ICONS.audio,
  m4a: FILE_ICONS.audio,
  ogg: FILE_ICONS.audio,
  flac: FILE_ICONS.audio,
  aiff: FILE_ICONS.audio,
  aif: FILE_ICONS.audio,
  mid: FILE_ICONS.audio,
  midi: FILE_ICONS.audio,
  pdf: FILE_ICONS.pdf,
  doc: FILE_ICONS.document,
  docx: FILE_ICONS.document,
  txt: FILE_ICONS.document,
  rtf: FILE_ICONS.document,
  md: FILE_ICONS.document,
  pages: FILE_ICONS.document,
  xls: FILE_ICONS.spreadsheet,
  xlsx: FILE_ICONS.spreadsheet,
  csv: FILE_ICONS.spreadsheet,
  tsv: FILE_ICONS.spreadsheet,
  numbers: FILE_ICONS.spreadsheet,
  ppt: FILE_ICONS.slideshow,
  pptx: FILE_ICONS.slideshow,
  key: FILE_ICONS.slideshow,
  json: FILE_ICONS.code,
  xml: FILE_ICONS.code,
  yml: FILE_ICONS.code,
  yaml: FILE_ICONS.code,
  html: FILE_ICONS.code,
  htm: FILE_ICONS.code,
  css: FILE_ICONS.code,
  scss: FILE_ICONS.code,
  js: FILE_ICONS.code,
  ts: FILE_ICONS.code,
  jsx: FILE_ICONS.code,
  tsx: FILE_ICONS.code,
  py: FILE_ICONS.code,
  sh: FILE_ICONS.code,
  zip: FILE_ICONS.archive,
  rar: FILE_ICONS.archive,
  '7z': FILE_ICONS.archive,
  tar: FILE_ICONS.archive,
  gz: FILE_ICONS.archive,
};

/**
 * API のファイルカテゴリ文字列（'image' / 'video' / 'audio' / 'document' など）からアイコン名を取得する。
 */
export const getFileIconFromCategory = (category?: string | null): string => {
  if (!category) return FILE_ICONS.unknown;
  return CATEGORY_ICON_MAP[category] ?? FILE_ICONS.unknown;
};

/**
 * MediaType（MediaPreview の型）からアイコン名を取得する。
 */
export const getFileIconFromMediaType = (mediaType?: string | null): string => {
  if (!mediaType) return FILE_ICONS.unknown;
  return MEDIA_TYPE_ICON_MAP[mediaType] ?? FILE_ICONS.unknown;
};

/**
 * MIME タイプからアイコン名を取得する（File.type や HTTP Content-Type など）。
 */
export const getFileIconFromMime = (mimeType?: string | null): string => {
  if (!mimeType) return FILE_ICONS.unknown;
  const type = mimeType.toLowerCase();
  if (type.startsWith('image/')) return FILE_ICONS.image;
  if (type.startsWith('video/')) return FILE_ICONS.video;
  if (type.startsWith('audio/')) return FILE_ICONS.audio;
  if (type.includes('pdf')) return FILE_ICONS.pdf;
  if (type.includes('word') || type.includes('document')) return FILE_ICONS.document;
  if (type.includes('excel') || type.includes('spreadsheet')) return FILE_ICONS.spreadsheet;
  if (type.includes('zip') || type.includes('compressed') || type.includes('x-tar') || type.includes('gzip')) {
    return FILE_ICONS.archive;
  }
  return FILE_ICONS.unknown;
};

/**
 * ファイル名または URL の拡張子からアイコン名を取得する。
 * クエリ文字列（?...）は自動で除去する。
 */
export const getFileIconFromExtension = (nameOrUrl?: string | null): string => {
  if (!nameOrUrl) return FILE_ICONS.unknown;
  const cleaned = nameOrUrl.split('?')[0];
  const ext = cleaned.split('.').pop()?.toLowerCase() ?? '';
  return EXTENSION_ICON_MAP[ext] ?? FILE_ICONS.unknown;
};

/**
 * File オブジェクト（ローカル選択ファイル）からアイコン名を取得する。
 * MIME タイプが空のときはファイル名の拡張子にフォールバックする。
 */
export const getFileIconFromFile = (file: File): string => {
  if (file.type) {
    const icon = getFileIconFromMime(file.type);
    if (icon !== FILE_ICONS.unknown) return icon;
  }
  return getFileIconFromExtension(file.name);
};

/**
 * API のファイルカテゴリ文字列で判定し、未知カテゴリの場合はファイル名 / URL の拡張子から推測する。
 *
 * 例: file_type が 'other' や空文字でも、fileName が hoge.xlsx なら table_chart になる。
 * それでも判定できない場合は汎用ファイルアイコン（FILE_ICONS.unknown）。
 */
export const getFileIconFromCategoryOrExtension = (
  category?: string | null,
  nameOrUrl?: string | null,
): string => {
  const fromCategory = getFileIconFromCategory(category);
  if (fromCategory !== FILE_ICONS.unknown) return fromCategory;
  return getFileIconFromExtension(nameOrUrl);
};
